/**
 * 미션 제출 페이지 — 인증 섹션
 *
 * 세 가지 인증 플로우:
 * 1. Setup code 비밀번호 설정 (선정 알림톡에서 ?code=xxx로 접속)
 * 2. 비밀번호 로그인
 * 3. 비밀번호 재설정 (SMS 인증 → 새 비밀번호 설정)
 */

import { useState } from 'react'
import type { PublicCampaignResponse } from '@/lib/seedingPublicApi'
import { seedingPublicApi } from '@/lib/seedingPublicApi'
import { formatPhoneInput, stripPhone } from '@/lib/phoneUtils'

// ==================== Setup Code 비밀번호 설정 ====================

interface SetupCodeFormProps {
  campaign: PublicCampaignResponse | null
  codeFromUrl: string
  setupCodePhone: string
  setupCodeCampaignTitle: string
  onSetupComplete: () => Promise<void>
}

export function SetupCodeForm({
  campaign,
  codeFromUrl,
  setupCodePhone,
  setupCodeCampaignTitle,
  onSetupComplete,
}: SetupCodeFormProps) {
  const [setupPassword, setSetupPassword] = useState('')
  const [setupConfirmPassword, setSetupConfirmPassword] = useState('')
  const [isSettingUp, setIsSettingUp] = useState(false)
  const [setupError, setSetupError] = useState<string | null>(null)

  const handleSetupCodePassword = async () => {
    if (!setupPassword || !setupConfirmPassword) {
      setSetupError('비밀번호를 입력해주세요.')
      return
    }
    if (setupPassword.length < 4) {
      setSetupError('비밀번호는 4자 이상이어야 합니다.')
      return
    }
    if (setupPassword !== setupConfirmPassword) {
      setSetupError('비밀번호가 일치하지 않아요.')
      return
    }
    setIsSettingUp(true)
    setSetupError(null)
    try {
      await seedingPublicApi.setPasswordWithCode(codeFromUrl, setupPassword)
      await onSetupComplete()
    } catch (err: any) {
      setSetupError(err.message || '비밀번호 설정에 실패했어요.')
    } finally {
      setIsSettingUp(false)
    }
  }

  return (
    <div className="min-h-screen bg-surface-lowest">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <h1 className="text-xl font-bold text-on-surface">
            {setupCodeCampaignTitle || campaign?.title || '미션 관리'}
          </h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="max-w-md mx-auto p-8">
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-on-surface mb-2">
              환영합니다! 비밀번호를 설정해주세요
            </h2>
            <p className="text-on-surface-variant">
              비밀번호 설정 후 미션 페이지에 바로 접속할 수 있어요.
            </p>
            {setupCodePhone && (
              <p className="text-sm text-on-surface-variant mt-2">
                전화번호: {setupCodePhone}
              </p>
            )}
          </div>

          {setupError && (
            <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-lg text-error text-sm">
              {setupError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                비밀번호
              </label>
              <input
                type="password"
                value={setupPassword}
                onChange={(e) => setSetupPassword(e.target.value)}
                placeholder="4자 이상"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                비밀번호 확인
              </label>
              <input
                type="password"
                value={setupConfirmPassword}
                onChange={(e) => setSetupConfirmPassword(e.target.value)}
                placeholder="비밀번호 재입력"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSetupCodePassword()
                }}
              />
            </div>
            <button
              onClick={handleSetupCodePassword}
              disabled={isSettingUp || !setupPassword || !setupConfirmPassword}
              className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dim transition-colors disabled:opacity-50"
            >
              {isSettingUp ? '설정 중...' : '설정 완료'}
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}

// ==================== 로그인 폼 (비밀번호 로그인 + 재설정) ====================

interface LoginFormProps {
  campaign: PublicCampaignResponse | null
  campaignId: string
  mallId: string
  shopNo: number
  error: string | null
  setupCodeUsedMessage: string | null
  initialPhone: string
  onLoginSuccess: () => Promise<void>
}

export function LoginForm({
  campaign,
  campaignId,
  mallId,
  shopNo,
  error,
  setupCodeUsedMessage,
  initialPhone,
  onLoginSuccess,
}: LoginFormProps) {
  // 비밀번호 로그인
  const [loginPhone, setLoginPhone] = useState(formatPhoneInput(initialPhone))
  const [loginPassword, setLoginPassword] = useState('')
  const [isPasswordLogging, setIsPasswordLogging] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)

  // 비밀번호 재설정 모드
  const [resetPasswordMode, setResetPasswordMode] = useState(false)

  // SMS 인증 (비밀번호 재설정 전용)
  const [phone, setPhone] = useState(formatPhoneInput(initialPhone))
  const [verifyCode, setVerifyCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  // 비밀번호 재설정 폼
  const [resetPassword, setResetPassword] = useState('')
  const [resetConfirmPassword, setResetConfirmPassword] = useState('')
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null)

  // 비밀번호 로그인 핸들러
  const handlePasswordLogin = async () => {
    if (!loginPhone || !loginPassword) {
      setLoginError('전화번호와 비밀번호를 입력해주세요.')
      return
    }
    setIsPasswordLogging(true)
    setLoginError(null)
    try {
      await seedingPublicApi.memberLogin(mallId, shopNo, stripPhone(loginPhone), loginPassword)
      await onLoginSuccess()
    } catch (err: any) {
      setLoginError(err.message || '로그인에 실패했어요.')
    } finally {
      setIsPasswordLogging(false)
    }
  }

  // SMS 인증코드 발송
  const handleSendCode = async () => {
    if (!phone) {
      setVerifyError('전화번호를 입력해주세요.')
      return
    }
    setIsSendingCode(true)
    setVerifyError(null)
    try {
      await seedingPublicApi.sendPhoneCode(stripPhone(phone))
      setCodeSent(true)
    } catch (err: any) {
      setVerifyError(err.message || '인증코드 발송에 실패했어요.')
    } finally {
      setIsSendingCode(false)
    }
  }

  // SMS 인증코드 확인
  const handleVerifyCode = async () => {
    if (!verifyCode) {
      setVerifyError('인증코드를 입력해주세요.')
      return
    }
    setIsVerifying(true)
    setVerifyError(null)
    try {
      await seedingPublicApi.verifyPhoneCode(stripPhone(phone), verifyCode)
      setPhoneVerified(true)
    } catch (err: any) {
      setVerifyError(err.message || '인증에 실패했어요.')
    } finally {
      setIsVerifying(false)
    }
  }

  // 비밀번호 재설정
  const handleResetPassword = async () => {
    if (!resetPassword || !resetConfirmPassword) {
      setResetPasswordError('비밀번호를 입력해주세요.')
      return
    }
    if (resetPassword.length < 4) {
      setResetPasswordError('비밀번호는 4자 이상이어야 합니다.')
      return
    }
    if (resetPassword !== resetConfirmPassword) {
      setResetPasswordError('비밀번호가 일치하지 않아요.')
      return
    }
    setIsResettingPassword(true)
    setResetPasswordError(null)
    try {
      // 캠페인 ID 는 부모가 넘겨준 prop 을 쓴다.
      // URL 경로 마지막 조각을 쓰면 short URL(`/{shortCode}/submit`) 에서 "submit" 이 잡혀
      // 서버가 신청을 못 찾고 "선정되지 않았습니다" 403 을 낸다.
      await seedingPublicApi.setPassword(mallId, shopNo, resetPassword, campaignId)
      setResetPasswordMode(false)
      await onLoginSuccess()
    } catch (err: any) {
      setResetPasswordError(err.message || '비밀번호 재설정에 실패했어요.')
    } finally {
      setIsResettingPassword(false)
    }
  }

  // 돌아가기 핸들러 (비밀번호 재설정 → 로그인)
  const handleBackToLogin = () => {
    setResetPasswordMode(false)
    setPhoneVerified(false)
    setCodeSent(false)
    setVerifyCode('')
    setVerifyError(null)
  }

  // SMS 인증 성공 후 비밀번호 재설정 폼 표시 여부
  const showResetForm = resetPasswordMode && phoneVerified

  return (
    <div className="min-h-screen bg-surface-lowest">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <h1 className="text-xl font-bold text-on-surface">
            {campaign?.title ? `${campaign.title} 미션 관리` : '미션 관리'}
          </h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/30 rounded-lg text-error">
            {error}
          </div>
        )}

        {setupCodeUsedMessage && (
          <div className="mb-6 p-4 bg-primary/10 border border-primary/30 rounded-lg text-primary text-sm">
            {setupCodeUsedMessage}
          </div>
        )}

        <div className="max-w-md mx-auto p-8">
          {showResetForm ? (
            /* 비밀번호 재설정 폼 (SMS 인증 완료 후) */
            <div>
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold text-on-surface mb-2">새 비밀번호 설정</h2>
                <p className="text-on-surface-variant">새로운 비밀번호를 입력해주세요.</p>
              </div>

              {resetPasswordError && (
                <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-lg text-error text-sm">
                  {resetPasswordError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1">새 비밀번호</label>
                  <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="4자 이상"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1">비밀번호 확인</label>
                  <input
                    type="password"
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    placeholder="비밀번호 재입력"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleResetPassword() }}
                  />
                </div>
                <button
                  onClick={handleResetPassword}
                  disabled={isResettingPassword || !resetPassword || !resetConfirmPassword}
                  className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dim transition-colors disabled:opacity-50"
                >
                  {isResettingPassword ? '설정 중...' : '비밀번호 재설정'}
                </button>
                <button onClick={handleBackToLogin} className="w-full py-2 text-sm text-on-surface-variant hover:text-on-surface">
                  ← 로그인으로 돌아가기
                </button>
              </div>
            </div>
          ) : resetPasswordMode ? (
            /* 비밀번호 재설정: 인라인 SMS 인증 */
            <div>
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold text-on-surface mb-2">비밀번호 재설정</h2>
                <p className="text-on-surface-variant">전화번호 인증 후 새 비밀번호를 설정할 수 있습니다.</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1">전화번호</label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneInput(e.target.value, phone))}
                      placeholder="010-1234-5678"
                      className="flex-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                      disabled={codeSent}
                    />
                    <button
                      type="button"
                      onClick={handleSendCode}
                      disabled={isSendingCode || !phone}
                      className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dim disabled:opacity-50 whitespace-nowrap"
                    >
                      {isSendingCode ? '발송중...' : codeSent ? '재발송' : '인증코드 발송'}
                    </button>
                  </div>
                </div>

                {codeSent && (
                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-1">인증코드</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value)}
                        placeholder="6자리 코드"
                        maxLength={6}
                        className="flex-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <button
                        type="button"
                        onClick={handleVerifyCode}
                        disabled={isVerifying || !verifyCode}
                        className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dim disabled:opacity-50"
                      >
                        {isVerifying ? '확인중...' : '확인'}
                      </button>
                    </div>
                    <p className="text-xs text-on-surface-variant mt-1">카카오톡으로 발송된 인증코드를 입력해주세요.</p>
                  </div>
                )}

                {verifyError && <p className="text-error text-sm">{verifyError}</p>}

                <button onClick={handleBackToLogin} className="w-full py-2 text-sm text-on-surface-variant hover:text-on-surface">
                  ← 로그인으로 돌아가기
                </button>
              </div>
            </div>
          ) : (
            /* 비밀번호 로그인 폼 */
            <div>
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold text-on-surface mb-2">본인 확인이 필요합니다</h2>
                <p className="text-on-surface-variant">미션을 제출하려면 로그인이 필요합니다.</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1">전화번호</label>
                  <input
                    type="tel"
                    value={loginPhone}
                    onChange={(e) => setLoginPhone(formatPhoneInput(e.target.value, loginPhone))}
                    placeholder="010-1234-5678"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-on-surface mb-1">비밀번호</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="비밀번호"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                    onKeyDown={(e) => { if (e.key === 'Enter') handlePasswordLogin() }}
                  />
                </div>

                <button
                  onClick={handlePasswordLogin}
                  disabled={isPasswordLogging || !loginPhone || !loginPassword}
                  className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dim transition-colors disabled:opacity-50"
                >
                  {isPasswordLogging ? '로그인 중...' : '로그인'}
                </button>

                {loginError && <p className="text-error text-sm">{loginError}</p>}

                <p className="text-xs text-on-surface-variant text-center">
                  비밀번호를 잊으셨나요?{' '}
                  <button
                    onClick={() => {
                      setResetPasswordMode(true)
                      setLoginError(null)
                      if (loginPhone) setPhone(loginPhone)
                    }}
                    className="text-primary hover:underline"
                  >
                    비밀번호 재설정
                  </button>
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
